import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { http } from "../lib/api";
import { Reveal, Label } from "./anim";
import { Clock, ArrowRight } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export const LatestWriting = () => {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await http.get("/posts?limit=3");
        setPosts(res.data.slice(0, 3));
      } catch (e) { /* ignore */ }
      finally { setLoaded(true); }
    })();
  }, []);

  if (loaded && posts.length === 0) return null;

  return (
    <section id="writing" data-testid="latest-writing-section" className="relative bg-[#EFEBDE] py-24 md:py-40 border-t border-[#16281C]/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <Reveal>
            <Label>Latest Writing</Label>
            <h2 className="mt-6 font-display font-semibold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-[#16281C] max-w-3xl">
              Notes from the field.
            </h2>
          </Reveal>
          <Link to="/blog" data-testid="view-all-posts" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#2C5F3A] hover:text-[#367A49] transition-colors self-start group">
            View all posts <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}>
              <Link to={`/blog/${p.slug}`} className="group flex flex-col h-full bg-[#FAF8F1] border border-[#16281C]/12 hover:border-[#2C5F3A]/40 transition-colors overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden bg-[#DDE4D5]">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#2C5F3A] to-[#16281C]" />
                  )}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  {p.tags?.length > 0 && <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#2C5F3A] mb-3">{p.tags[0]}</span>}
                  <h3 className="font-display font-semibold text-xl tracking-tight text-[#16281C] leading-tight group-hover:text-[#2C5F3A] transition-colors">{p.title}</h3>
                  {p.excerpt && <p className="mt-3 font-body text-sm text-[#66766A] leading-relaxed line-clamp-2">{p.excerpt}</p>}
                  <div className="mt-auto pt-6 flex items-center justify-between font-mono text-[10px] tracking-[0.15em] uppercase text-[#8B978C]">
                    <span>{fmtDate(p.published_at)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {p.read_time} min</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
